/* eslint-disable no-console */
/* eslint-disable func-names */

/**
 * @fileOverview Check Selectra's call centers status
 * @author Selectra Front End Team
 * @version 1.0.0
 * @requires Polyfill:Fetch
 * @requires Polyfill:Object.keys
 * @requires Polyfill:String.prototype.repeat
 */

/**
 * @async
 * @function getCallCenterStatus
 * @description Retrieves call center status via the Dialga API
 * @param {object} options
 * @param {Array} options.groupsIDs - Array of call-center integer IDs.
 * Check '/src/data/callcenter-ids.json' for the current list
 * @param {string} [options.operation=schedule] - Either 'status' or 'schedule'
 * 'status' will return 'true' for currently opened, false otherwise.
 * 'schedule' operation will return an object.
 * @returns {promise} A call-center status object.
 * @param opts
 */
export default async function getCallCenterStatus( opts ) {

  const

    // Set default options
    defaultOpts = {
      groupIDs: [],
      operation: 'schedule',
    },
    options = { ...defaultOpts, ...opts },

    // Base URLs for the Dialga API endpoints
    apiBaseUrlOpen =
      'https://dialga.selectra.io/api/schedules/current_open_departments',
    apiBaseUrlDate =
      'https://dialga.selectra.io/api/schedules/schedule_on_date',

    // Retrieve current date values
    date = new Date(),
    year = date.getFullYear(),
    month = padNum( date.getMonth() + 1 ),
    day = padNum( date.getDate()),
    hour = padNum( date.getHours()),
    minute = padNum( date.getMinutes()),

    // Error object
    error = {
      id: null,
      status: null,
      url: null,
    };

  let
    response,
    retval = {},
    apiEndPoint;

  // Set endpoint URL & Fetch response
  switch ( options.operation ) {
  case 'status':
    apiEndPoint =
      `${ apiBaseUrlOpen }?by_id=true`;
    break;
  case 'schedule':
    apiEndPoint =
      `${ apiBaseUrlDate }?date=${ year }-${ month }-${ day }&by_id=true`;
    break;
  }

  response =
    await fetch( apiEndPoint, { method: 'GET', mode: 'cors' })
      .then( response => {
        const status = response.status;
        if ( status === 200 ) {
          return response.json();
        } else if ( status ) {
          error.id = 'FetchError';
          error.status = status;
          error.url = response.url;
          throw error;
        }
      })
      .then( json => {
        // Return only the groups we are interested in
        let filteredResponse = {};

        if ( !options.groupIDs.length ) {
          filteredResponse = { ...json };
        } else {
          for ( let i = 0; i < options.groupIDs.length; ++i ) {
            const currID = options.groupIDs[ i ];
            if ( currID in json ) {
              filteredResponse[ currID ] = json[ currID ];
            }
          }
        }

        if ( !Object.keys( filteredResponse ).length ) {
          // Only non existent IDs were requested...
          error.id = 'BadGroupIDs';
          throw error;
        } else {
          return filteredResponse;
        }

      })
      .catch( error => {
        if ( error.id === 'fetchError' ) {
          console.error( `${ error.status }: Could not GET ${ apiEndPoint }` );
        } else if ( error.id === 'BadGroupIDs' ) {
          console.error( 'Not recognized group IDs' );
        } else {
          console.error( `No response from ${ apiEndPoint }` );
          error.id = 'NetworkError';
        }
        throw error;
      });

  if ( options.operation === 'schedule' ) {

    const totalGroups = options.groupIDs.length;

    // Check if call centers are open
    for ( let i = 0; i < totalGroups; ++i ) {

      const
        group = options.groupIDs[ i ],
        newObj = { ...response[ group ] };

      if ( !Object.keys( newObj ).length ) {
        // There's no schedule objects, the call center is closed all day
        newObj.state = 'closed';
      } else if ( 0 in newObj ) {
        // An schedule object is found.
        // Can be the schedule for the whole day or the morning one
        newObj.state =
          testSchedule( hour, minute, newObj[ 0 ]) ? 'open' : 'closed';
        if ( newObj.state === 'closed' && 1 in newObj ) {
          // It's a split schedule, repeat test if CC is 'closed'
          newObj.state =
            testSchedule( hour, minute, newObj[ 1 ]) ? 'open' : 'closed';
        }
      }
      retval[ group ] = newObj;
    }
  }

  return retval;

}


/**
 * @function testSchedule
 * @description Check if the specified time is inside the schedule range.
 * @param {string} currentHour - A 2 digit string representing the current hour.
 * In the range '00' -> '24'
 * @param {string} currentMinute - A 2 digit string representing the current
 * minute. In the range '00' -> '60'
 * @param {object} scheduleObj - A schedule object
 * @param {string} scheduleObj.open - Start time if the schedule. Format: 'hh:mm'
 * @param {string} scheduleObj.close - End time of the schedule. Format: 'hh:mm'
 * @returns {boolean} True if passed time is in schedule, False otherwise
 */
function testSchedule( currentHour, currentMinute, scheduleObj ) {
  const
    scheduleStart = scheduleObj.open.replace( ':', '' ),
    scheduleEnd = scheduleObj.close.replace( ':', '' );

  delete scheduleObj.id;

  return (
    `${ currentHour }${ currentMinute }` > scheduleStart &&
    `${ currentHour }${ currentMinute }` < scheduleEnd
  );

}


/**
 * @function padNum
 * @description Convert a number into a string with leading zeroes.
 * @param {number} number - The number to convert.
 * @param {number} [length=2] - Total number of digits for the padded string.
 * @returns {string}
 */
function padNum( number, length = 2 ) {
  return (( '0'.repeat( length )) + number ).slice( -length );
}
